/**
 * The unit each satoshi-denominated symbol is read in, decided once.
 *
 * The datafeed learns it from the first page of bars for a symbol; the price
 * formatter, the legend, the hover label and the overlay tooltips all read it
 * from here. It is module-level for the same reason the band store is: the
 * library's price formatter is created before any bar exists and asked to
 * format at render time, so the decision has to live somewhere both can see.
 */

import { parseSymbol } from './symbol'
import { unitForSats, type PriceUnit } from './price-format'

const units = new Map<string, PriceUnit>()

export function unitFor(symbol: string): PriceUnit {
	return units.get(symbol) ?? 'sats'
}

/**
 * Decide the unit for a symbol from its bars, once. Later pages do not move
 * it: a viewer scrolling into a cheaper past keeps the axis they started
 * with, and a collection that has crossed 0.001 BTC keeps reading in BTC
 * until it is opened again.
 */
export function learnUnit(symbol: string, closesSats: number[]): PriceUnit {
	const known = units.get(symbol)
	if (known) {
		return known
	}
	if (parseSymbol(symbol)?.denom !== 'sats') {
		return 'sats'
	}
	const sorted = closesSats.filter((v) => Number.isFinite(v) && v > 0).sort((a, b) => a - b)
	if (sorted.length === 0) {
		return 'sats'
	}
	const unit = unitForSats(sorted[Math.floor(sorted.length / 2)])
	units.set(symbol, unit)
	return unit
}

export function forgetUnit(symbol: string) {
	units.delete(symbol)
}
